import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHelmet from '../../components/SEOHelmet';
import { OrganizationSchema, WebsiteSchema, BreadcrumbSchema } from '../../components/SchemaMarkup';
import { mainPageSeo } from '../../data/seoData';
import { chapters, atlas, heroImage } from '../../data/homeContent';
import './Home.css';

const rise = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const Home = () => {
  const homeSeo = mainPageSeo.home;

  return (
    <div className="home">
      <SEOHelmet
        title={homeSeo.title}
        description={homeSeo.description}
        keywords={homeSeo.keywords}
        image={homeSeo.image}
        url="https://safarnamah.in"
        type="website"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <BreadcrumbSchema items={[{ name: 'Home', url: 'https://safarnamah.in' }]} />

      {/* Opening frame */}
      <section className="frame frame-open">
        <div className="frame-bg" style={{ backgroundImage: `url(${heroImage})` }} />
        <div className="frame-scrim" />
        <div className="frame-inner">
          <div className="container">
            <motion.div initial="hidden" animate="show" variants={rise}>
              <span className="eyebrow">Asia &nbsp;&middot;&nbsp; 10 countries &nbsp;&middot;&nbsp; since 2019</span>
              <h1 className="frame-title">
                We went.<em>Then we wrote it down.</em>
              </h1>
              <p className="frame-sub">
                Honest guides, real detail and photographs from every trip Saket &amp; Pallavi
                have taken across Asia.
              </p>
              <div className="frame-actions">
                <Link to="/places" className="btn btn-primary">Explore the atlas</Link>
                <Link to="/plan-trip" className="btn btn-ghost">Plan your trip</Link>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="frame-foot">
          <div className="container frame-foot-in">
            <div className="stats">
              <div><b>10</b><span>Countries</span></div>
              <div><b>{atlas.length}</b><span>Guides</span></div>
              <div><b>7</b><span>Years travelling</span></div>
            </div>
            <span className="scroll-hint"><i />Scroll</span>
          </div>
        </div>
      </section>

      {/* Chapters - each one links through to its guide */}
      {chapters.map((c, i) => (
        <section className={`frame frame-chapter${i % 2 ? ' frame-alt' : ''}`} key={c.id}>
          <div className="frame-bg" style={{ backgroundImage: `url(${c.image})` }} />
          <div className="frame-scrim" />
          <div className="frame-inner">
            <div className="container">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.35 }}
                variants={rise}
              >
                <span className="chapter-num">{c.number} / {c.country.toUpperCase()}</span>
                <h2 className="frame-title frame-title-sm">{c.title}</h2>
                <p className="frame-sub">{c.blurb}</p>
                <dl className="facts">
                  {c.facts.map((f) => (
                    <div key={f.label}>
                      <dt>{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>
                <Link to={`/places/${c.id}`} className="cta-line">
                  Read the {c.country} guide &nbsp;&rarr;
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Handoff into the atlas */}
      <section className="handoff">
        <div className="container">
          <span className="eyebrow eyebrow-ink">Every country we&rsquo;ve written up</span>
          <h2>Where are you going?</h2>
          <p>
            Each guide covers what things cost, when to go, how we got around and the
            mistakes we made so you don&rsquo;t have to repeat them.
          </p>
        </div>
      </section>

      {/* Atlas rail */}
      <section className="rail">
        <div className="rail-track">
          {atlas.map((d) => (
            <Link className="slide" to={`/places/${d.id}`} key={d.id}>
              <div className="slide-photo" style={{ backgroundImage: `url(${d.image})` }} />
              <h3>{d.name}</h3>
              <p>{d.region}</p>
            </Link>
          ))}
        </div>
        <div className="container rail-more">
          <Link to="/places" className="cta-line cta-line-ink">See all destinations &nbsp;&rarr;</Link>
        </div>
      </section>

      {/* Closing */}
      <section className="closing">
        <div className="container">
          <span className="eyebrow">Plan yours</span>
          <h2>Tell us where you want to go.</h2>
          <p>
            Send us your dates and budget and we&rsquo;ll come back with a personalised
            itinerary built from trips we have actually taken.
          </p>
          <Link to="/plan-trip" className="btn btn-light">Start planning</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
